import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Radio from "@mui/material/Radio";
import Divider from "@mui/material/Divider";
import QuestionPreview from "../QuestionPreview";
import { useSelector } from "react-redux";
import {
  THEME_WALMART_CLASSIC,
  THEME_GLOBAL_TECHNOLOGY,
  THEME_SAM_CLUB,
} from "../../constants";

import useStyles from "./styles";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";

const AnswerMatrix = (props) => {
  const { t } = useTranslation("surveyTaker");

  const styles = useStyles(props);
  const [state, setState] = useState(props.value);
  const [questionIdState, setQuestionIdState] = useState(props.questionId);
  const columns = _.cloneDeep(props.column);
  columns && columns.unshift("");

  let surveyTheme = THEME_WALMART_CLASSIC;
  const { currentSurveyId, data } = useSelector((state) => state.survey);
  if (
    currentSurveyId &&
    data[currentSurveyId] &&
    data[currentSurveyId].thirdPartySettings
  ) {
    const { theme } = data[currentSurveyId].thirdPartySettings;
    if (theme) {
      surveyTheme = theme;
    } else {
      surveyTheme = THEME_WALMART_CLASSIC;
    }
  }

  const isMobile = window.innerWidth < 768;

  useEffect(() => {
    if (questionIdState !== props.questionId) {
      setQuestionIdState(props.questionId);
      setState(props.value);
    }
  }, [props.questionId]);

  useEffect(() => {
    setState(props.value);
  }, [props.value]);

  const handleChange = (row, column, index) => {
    setState((prevState) => ({ ...prevState, [index]: { column, row } }));

    const tempState = _.cloneDeep(state);
    tempState[index] = { column, row };
    const answerList = [];
    const sorted_keys = Object.keys(tempState).sort((a, b) => a - b);
    sorted_keys.forEach((key) => {
      answerList.push({
        question: tempState[key]?.row || "",
        answer: tempState[key]?.column || "",
        index: key,
        questionType: "matrix",
      });
    });
    props.handleChange(answerList);
  };

  return (
    <fieldset>
      <div className={styles.headingStyle}>
        <QuestionPreview
          question={props.question || t("surveyTaker:answerMatrix")}
          required={props.required}
          helperText={props.helperText}
          matrix={true}
        />
      </div>
      {isMobile ? (
        <>
          {props.row &&
            props.row.map((rowItem, index) => (
              <Fragment key={rowItem._id} tabindex={0}>
                <h2 className={styles.mobileQuestionLabel} key={rowItem._id}>
                  {rowItem.label}
                </h2>
                {props.column.map((item) => {
                  const rowItemValue = rowItem.value.replace("Value_", "");
                  const colItemValue = item.value.replace("Value_", "");
                  return (
                    <Fragment key={item._id}>
                      <div className={styles.mobileOptionWrapper}>
                        <label
                          className={styles.mobileOptionLabel}
                          htmlFor={rowItem.label}
                        >
                          {item.label}
                        </label>
                        <div className={styles.radioContainer}>
                          <Radio
                            classes={{
                              root:
                                state[index]?.column === colItemValue
                                  ? styles.elementStyle
                                  : styles.elementStyleNotSelected,
                              checked:
                                surveyTheme === THEME_GLOBAL_TECHNOLOGY
                                  ? styles.globalTech
                                  : surveyTheme === THEME_SAM_CLUB
                                  ? styles.samClub
                                  : styles.walmartClassic,
                            }}
                            checked={state[index]?.column === colItemValue}
                            value={colItemValue}
                            name={rowItem.label}
                            radioGroup={rowItem.label}
                            id={rowItem.label}
                            inputProps={{
                              "aria-label": `${rowItem.label},${item.label}`,
                              "aria-describedby": `${props.questionId}-error`,
                            }}
                            onChange={() =>
                              handleChange(rowItemValue, colItemValue, index)
                            }
                          />
                        </div>
                      </div>
                      <Divider />
                    </Fragment>
                  );
                })}
              </Fragment>
            ))}
        </>
      ) : (
        <div className={styles.desktopTableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                {columns &&
                  columns.map((item, index) => (
                    <TableCell
                      key={index}
                      component={index ? "" : "th"}
                      className={`${styles.desktopTableCellHeader}
                      ${index === 0 ? styles.desktopTableCellQuestion : ""}
                      `}
                    >
                      {item.label}
                    </TableCell>
                  ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {props.row &&
                props.row.map((rowItem, index) => (
                  <TableRow key={rowItem._id}>
                    <TableCell className={styles.desktopTableCellQuestion}>
                      {rowItem.label}
                    </TableCell>
                    {props.column &&
                      props.column.map((item) => {
                        const rowItemValue = rowItem.value.replace(
                          "Value_",
                          ""
                        );
                        const colItemValue = item.value.replace("Value_", "");
                        return (
                          <TableCell
                            className={styles.desktopTableCellBody}
                            key={item._id}
                          >
                            <Radio
                              classes={{
                                root:
                                  state[index]?.column === colItemValue
                                    ? styles.elementStyle
                                    : styles.elementStyleNotSelected,
                                checked:
                                  surveyTheme === THEME_GLOBAL_TECHNOLOGY
                                    ? styles.globalTech
                                    : surveyTheme === THEME_SAM_CLUB
                                    ? styles.samClub
                                    : styles.walmartClassic,
                              }}
                              checked={state[index]?.column === colItemValue}
                              value={colItemValue}
                              name={rowItem.label}
                              radioGroup={rowItem.label}
                              inputProps={{
                                "aria-label": `${rowItem.label}, ${item.label}`,
                                "aria-describedby": `${props.questionId}-error`,
                              }}
                              onChange={() =>
                                handleChange(rowItemValue, colItemValue, index)
                              }
                            />
                          </TableCell>
                        );
                      })}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      )}
    </fieldset>
  );
};

AnswerMatrix.defaultProps = {
  column: [],
  row: [],
  required: false,
  handleChange: () => {},
};

AnswerMatrix.propTypes = {
  column: PropTypes.array.isRequired,
  row: PropTypes.array.isRequired,
  question: PropTypes.string.isRequired,
  required: PropTypes.bool.isRequired,
  handleChange: PropTypes.func.isRequired,
};

export default AnswerMatrix;
